import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const isFailedLog = log => {
  if (!log) return false;
  if (log.errorMessage) return true;
  if (typeof log.statusCode === 'number' && log.statusCode >= 400) return true;
  return false;
};

const tryFormatJson = value => {
  if (!value) return null;

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;

    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return value;
    }
  }

  return String(value);
};

const formatDate = value => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString();
};

const StatusChip = ({ statusCode }) => {
  if (statusCode == null) {
    return <Chip label="Unknown" size="small" variant="outlined" />;
  }

  let color = 'default';
  if (statusCode >= 500) color = 'error';
  else if (statusCode >= 400) color = 'warning';
  else if (statusCode >= 200) color = 'success';

  return (
    <Chip
      label={statusCode}
      size="small"
      color={color}
      variant="outlined"
    />
  );
};

const LogDetailsBlock = ({ title, value }) => {
  const formatted = tryFormatJson(value);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        {title}
      </Typography>

      <Box
        component="pre"
        sx={{
          m: 0,
          p: 2,
          borderRadius: 1,
          bgcolor: '#111827',
          color: '#e5e7eb',
          overflowX: 'auto',
          fontSize: '0.85rem',
          lineHeight: 1.5,
          fontFamily: 'Monaco, Consolas, "Courier New", monospace',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {formatted || 'No data'}
      </Box>
    </Box>
  );
};

const LogRow = ({ log }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow hover>
        <TableCell width={60}>
          <IconButton size="small" onClick={() => setOpen(prev => !prev)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{formatDate(log.addedOn || log.createdAt)}</TableCell>
        <TableCell>{log.entityName || '-'}</TableCell>
        <TableCell>{log.entityKey || '-'}</TableCell>
        <TableCell>{log.httpMethod || '-'}</TableCell>
        <TableCell>{log.route || '-'}</TableCell>
        <TableCell>
          <StatusChip statusCode={log.statusCode} />
        </TableCell>
        <TableCell>{log.errorMessage || '-'}</TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, borderBottom: open ? undefined : 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, bgcolor: '#fafafa' }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Log Details
                </Typography>

                <Typography variant="body2">
                  <strong>Correlation Id:</strong> {log.correlationId || '-'}
                </Typography>
                <Typography variant="body2">
                  <strong>Triggered By User Id:</strong> {log.triggeredByUserId || '-'}
                </Typography>
                <Typography variant="body2">
                  <strong>Query String:</strong> {log.queryString || '-'}
                </Typography>
              </Box>

              <LogDetailsBlock title="Request Body" value={log.requestBody} />
              <LogDetailsBlock title="Response Body" value={log.responseBody} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const AcumaticaLogs = props => {
  const { failedLogs = [] } = props;
  const [showFailedOnly, setShowFailedOnly] = useState(true);

  const filteredLogs = useMemo(() => {
    if (showFailedOnly) {
      return failedLogs.filter(isFailedLog);
    }

    return failedLogs;
  }, [failedLogs, showFailedOnly]);

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Acumatica Logs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredLogs.length} of {failedLogs.length} logs
          </Typography>
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={showFailedOnly}
              onChange={e => setShowFailedOnly(e.target.checked)}
            />
          }
          label="Show failed logs only"
        />
      </Box>

      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Time</TableCell>
              <TableCell>Entity</TableCell>
              <TableCell>Entity Key</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Route</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Error</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredLogs.length > 0 ? (
              filteredLogs.map((log, index) => (
                <LogRow
                  key={log.correlationId || `${log.route}-${index}`}
                  log={log}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" sx={{ py: 3, color: 'text.secondary' }}>
                    No logs found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AcumaticaLogs;